import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { FileText, Calendar } from "lucide-react";
import DateDropdownPicker from "../ui/DateDropdownPicker";

const ConsumerDeclarationSection = ({ data, onChange, personalInfo, brokerData }) => {
  // Auto-populare date când componenta se montează sau când datele se schimbă
  useEffect(() => {
    if (personalInfo && personalInfo.firstName && personalInfo.lastName) {
      const fullName = `${personalInfo.firstName} ${personalInfo.lastName}`;
      
      // Auto-populează numele consumatorului dacă nu este deja completat
      if (!data.consumerName) {
        onChange({ consumerName: fullName });
      }
      
      // Auto-populează semnătura electronică cu același nume
      if (!data.consumerSignature) {
        onChange({ consumerSignature: fullName });
      }
    }
    
    // Auto-populează data curentă dacă nu există
    if (!data.signatureDate) {
      const today = new Date().toISOString().split('T')[0];
      onChange({ signatureDate: today });
    }
  }, [personalInfo]);

  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleCheckboxChange = (field, checked) => {
    onChange({ [field]: checked });
  };

  const companyName = brokerData?.companyName || brokerData?.companyData?.companyName || "societatea";
  
  // Formatează data curentă pentru afișare în text (ex: "12 decembrie 2024")
  const getCurrentDateFormatted = () => {
    const months = [
      "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
      "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"
    ];
    const today = new Date();
    return `${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`;
  };

  return (
    <div className="space-y-6">
      {/* Declarația consumator pentru reducerea perioadei de reflecție */}
      <Card className="border-orange-200 bg-orange-50/30">
        <CardHeader className="bg-gradient-to-r from-orange-100 to-yellow-100 border-b border-orange-200">
          <CardTitle className="flex items-center gap-2 text-orange-900">
            <FileText className="h-6 w-6" />
            Declarație Consumator - Reducerea Perioadei de Reflecție
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-white p-6 rounded-lg border border-orange-200">
            <div className="text-sm text-gray-800 leading-relaxed space-y-4">
              <h3 className="font-bold text-lg text-center text-orange-900 mb-6">
                Declarație consumator
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="consumerName">Subsemnatul</Label>
                  <Input
                    id="consumerName"
                    value={data.consumerName || ""}
                    onChange={(e) => handleInputChange('consumerName', e.target.value)}
                    placeholder="Nume și prenume complet"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="consumerAddress">cu domiciliul în</Label>
                  <Input
                    id="consumerAddress"
                    value={data.consumerAddress || ""}
                    onChange={(e) => handleInputChange('consumerAddress', e.target.value)}
                    placeholder="Adresa completă de domiciliu"
                  />
                </div>
              </div>

              <p className="font-semibold text-center mb-6">
                în calitate de consumator declar următoarele:
              </p>

              {/* Opțiuni pentru reducerea perioadei de reflecție */}
              <div className="space-y-4 mb-6">
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="reduceReflectionPeriod"
                        checked={data.reduceReflectionPeriod || false}
                        onCheckedChange={(checked) => {
                          handleCheckboxChange('reduceReflectionPeriod', checked);
                          if (!checked) {
                            handleCheckboxChange('noReductionReflectionPeriod', false);
                          }
                        }}
                        className="mt-1"
                      />
                      <Label htmlFor="reduceReflectionPeriod" className="text-sm leading-relaxed">
                        doresc să apelez la serviciile de intermediere oferite de <strong>{companyName}</strong> și optez 
                        în mod expres pentru reducerea perioadei legale de 5 zile calendaristice prevăzute înainte de 
                        desfășurarea activității de intermediere. Solicit ca activitatea de intermediere să poată începe 
                        fără a aștepta expirarea integrală a acestui termen și îmi exprim acordul pentru furnizarea 
                        informațiilor necesare în vederea identificării unor oferte/produse de credit;
                      </Label>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox 
                        id="noReductionReflectionPeriod"
                        checked={data.noReductionReflectionPeriod || false}
                        onCheckedChange={(checked) => {
                          handleCheckboxChange('noReductionReflectionPeriod', checked);
                          if (checked) {
                            handleCheckboxChange('reduceReflectionPeriod', false);
                            handleInputChange('reducedDays', '');
                          }
                        }}
                        className="mt-1"
                      />
                      <Label htmlFor="noReductionReflectionPeriod" className="text-sm leading-relaxed">
                        nu optez pentru reducerea perioadei legale de 5 zile calendaristice și doresc ca activitatea 
                        de intermediere să înceapă după expirarea acestei perioade.
                      </Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Declarațiile / confirmări consumator */}
              <div className="space-y-4">
                {[
                  `confirm că am primit și am luat la cunoștință Informarea consumator furnizată de ${companyName};`,
                  `am luat la cunoștință că ${companyName} este intermediar de credite nelegat, nu prestează servicii de consiliere și este remunerat de către creditori pentru activitatea de intermediere;`,
                  `am luat la cunoștință că nu datorez ${companyName} comision pentru serviciile de intermediere descrise în Informarea consumator;`,
                  `am luat la cunoștință că pot solicita informații privind variațiile nivelului comisioanelor plătite intermediarului de către diferiți creditori ai căror produse îmi sunt propuse;`,
                  `confirm că informațiile și documentele furnizate pe parcursul procesului de intermediere sunt și vor fi corecte, complete și actuale;`,
                  `am luat la cunoștință că decizia de aprobare sau respingere a cererii de credit aparține exclusiv creditorului;`,
                  `am luat la cunoștință că, dacă creditorul nu poate efectua evaluarea bonității sau verificările necesare din cauza lipsei informațiilor/documentelor necesare, creditul poate să nu fie acordat;`,
                  `am luat la cunoștință că pentru produsul de credit îmi vor fi furnizate informațiile precontractuale și FEIS, în condițiile prevăzute de OUG nr. 52/2016;`,
                  `confirm că am fost informat cu privire la prelucrarea datelor cu caracter personal necesare analizării și intermedierii cererii de credit, conform informării privind protecția datelor pusă la dispoziție de ${companyName}.`,
                ].map((txt, i) => (
                  <div key={i} className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <p className="text-sm leading-relaxed">{txt}</p>
                  </div>
                ))}

                <div className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold mt-1">•</span>
                  <p className="text-sm leading-relaxed">
                    <strong>solicit ca informațiile și documentele transmise pe parcursul procesului de intermediere a 
                    creditului să îmi fie comunicate:</strong>
                  </p>
                </div>

                {/* Opțiuni pentru comunicarea informațiilor */}
                <div className="ml-6 space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="communicateByEmail"
                      checked={data.communicateByEmail || false}
                      onCheckedChange={(checked) => handleCheckboxChange('communicateByEmail', checked)}
                    />
                    <Label htmlFor="communicateByEmail" className="text-sm">
                      în format electronic la adresa de email: <strong>{personalInfo?.email || "................................"}</strong>
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="communicateInWriting"
                      checked={data.communicateInWriting || false}
                      onCheckedChange={(checked) => handleCheckboxChange('communicateInWriting', checked)}
                    />
                    <Label htmlFor="communicateInWriting" className="text-sm">
                      în scris, în format fizic
                    </Label>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-300">
                <p className="text-center text-sm mb-6">
                  Prezentul act a fost semnat astăzi, <strong>{getCurrentDateFormatted()}</strong>, în 2 (două) exemplare, 
                  câte un exemplar pentru fiecare parte.
                </p>
                
                <div className="flex justify-between">
                  <div className="text-center">
                    <p className="font-semibold">
                      {brokerData?.companyName || "SC [NUME FIRMA BROKER]"}
                    </p>
                    <p>{brokerData?.representativeName || "[NUME REPREZENTANT]"}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">Consumator</p>
                    <p className="font-medium">{data.consumerName || "_____________________"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Confirmări finale obligatorii */}
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="font-semibold text-red-800 mb-3">Confirmări finale obligatorii:</p>
            
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="finalDataAccuracy"
                  checked={data.finalDataAccuracy || false}
                  onCheckedChange={(checked) => handleCheckboxChange('finalDataAccuracy', checked)}
                  className="mt-1"
                />
                <Label htmlFor="finalDataAccuracy" className="text-sm leading-relaxed">
                  <strong>Declar că datele sunt corecte și conforme cu realitatea.</strong>
                </Label>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="finalDataProcessingConsent"
                  checked={data.finalDataProcessingConsent || false}
                  onCheckedChange={(checked) => handleCheckboxChange('finalDataProcessingConsent', checked)}
                  className="mt-1"
                />
                <Label htmlFor="finalDataProcessingConsent" className="text-sm leading-relaxed">
                  <strong>Sunt de acord cu prelucrarea și transmiterea datelor către băncile partenere.</strong>
                </Label>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox 
                  id="finalNoFirmOffer"
                  checked={data.finalNoFirmOffer || false}
                  onCheckedChange={(checked) => handleCheckboxChange('finalNoFirmOffer', checked)}
                  className="mt-1"
                />
                <Label htmlFor="finalNoFirmOffer" className="text-sm leading-relaxed">
                  <strong>Confirm că acest formular nu reprezintă o ofertă fermă de credit.</strong>
                </Label>
              </div>
            </div>
          </div>

          {/* Semnătura electronică */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="consumerSignature">Semnătura Electronică Consumator</Label>
              <Input
                id="consumerSignature"
                value={data.consumerSignature || ""}
                onChange={(e) => handleInputChange('consumerSignature', e.target.value)}
                placeholder="Nume complet pentru semnătură electronică"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signatureDate">Data Semnării</Label>
              <DateDropdownPicker
                id="signatureDate"
                value={data.signatureDate || new Date().toISOString().split('T')[0]}
                onChange={(val) => handleInputChange('signatureDate', val)}
                maxYear={new Date().getFullYear() + 1}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsumerDeclarationSection;